import {
  errorMap,
  Form,
  Main,
  Padding,
  Stack,
  TotalEntries,
  useDirectory,
  DataObjectDescriptionFilter,
  zValidPeriodWithBothRequired,
  TimeSeriesFilters,
  EMPTY_PAGE,
  useValidationDashboardsQuery,
  DashboardType,
  VISUAL_VALIDATION_FILTER_KEYS,
  CustomPagination,
  type Frequency,
  useDataObjectGroupsWithDescriptionsQuery,
  DataObjectGroupType,
  useGroupDescriptionOptions,
  useGenerateVisualValidationTimeSeriesMutation,
  useLatestDataObjectVersionsQuery,
  useDuplicateVisualValidationTimeSeriesMutation,
  VisualValidationFilters,
  useSystemClassifierSelectOptions,
  useChangeVisualValidationTimeSeriesDataMutation,
  type ISODate,
  useVisualValidationDashboardActions,
  ValidationDashboardListDto,
  InfoIconUrl,
} from '@atlas-frontend/sdk';
import styled from 'styled-components';
import { path } from '../../../paths';

import translations from './TimeSeries.translations.json';
import { i18nNsFactory } from '@atlas-frontend/i18n';
import z from 'zod/v3';
import { Divider, Flex } from 'antd';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { VisualValidationHeader } from '../VisualValidationHeader';
import { VisualValidationActionButtons } from '../VisualValidationActionButtons';
import { keepPreviousData } from '@tanstack/react-query';
import { ValidationDashboardsTable } from '../ValidationDashboardsTable';
import { useFormContext, useWatch } from 'react-hook-form';
import { CommentsModal, hasDescriptionId } from '../modals/CommentsModal';
import {
  AlgorithmsModal,
  ModalData,
  SourcesModal,
} from '../modals/DetailsModal';

const { addTranslations, t } = i18nNsFactory('visual-validation-time-series');
addTranslations(translations);

type TimeSeriesFilters = VisualValidationFilters & {
  versionRule?: string;
  valueDateFrom?: string;
  valueDateTo?: string;
};

const FILTER_KEYS: (keyof TimeSeriesFilters)[] = [
  ...VISUAL_VALIDATION_FILTER_KEYS,
  'valueDateFrom',
  'valueDateTo',
  'versionRule',
];

export const TimeSeriesFormSchema = z
  .object({
    groupDescriptionRef: z
      .string(errorMap(() => t('validation.groupDescriptionRef.required')))
      .min(1),
    valueDateRange: zValidPeriodWithBothRequired(),
    versionRule: z
      .string(errorMap(() => t('validation.versionRule.required')))
      .min(1),
    dataObjectVersionIds: z.number().int().positive().array(),
    outliers: z.boolean().optional(),
    hodricPrescotti: z.boolean().optional(),
  })
  .superRefine((data, ctx) => {
    if (
      !Array.isArray(data.dataObjectVersionIds) ||
      !data.dataObjectVersionIds.length
    ) {
      ctx.addIssue({
        code: 'custom',
        path: ['versionRule'],
        message: t('validation.versionRule.noVersionsFound'),
      });
    }
  });

type TimeSeriesFormType = z.infer<typeof TimeSeriesFormSchema>;
interface TimeSeriesRequestData {
  dataObjectVersionIds: number[];
  valueDateFrom: ISODate;
  valueDateTo: ISODate;
  ruleCode: string;
  outliers: boolean;
  hp: boolean;
}

export const TimeSeries = () => (
  <Form schema={TimeSeriesFormSchema} blockNavigationOnFirstChange={false}>
    <Main>
      <TimeSeriesInner />
    </Main>
  </Form>
);

const TimeSeriesInner = () => {
  const { TIME_SERIES_VERSION: timeSeriesVersionOptions } =
    useSystemClassifierSelectOptions(['TIME_SERIES_VERSION'], 'code');

  const {
    queryParameters,
    setQueryFilters,
    tableProps,
    headerProps,
    setTableFilters,
    paginationProps,
  } = useDirectory({
    queryFilterKeys: FILTER_KEYS,
    initialFilters: {
      versionRule: timeSeriesVersionOptions[0]?.value as string | undefined,
    },
  });
  const { handleSubmit, getValues, trigger } =
    useFormContext<TimeSeriesFormType>();
  const [isCommentsModalOpen, setIsCommentsModalOpen] = useState(false);
  const [isLoadingDashboard, setIsLoadingDashboard] = useState(false);
  const [algorithmsModalData, setAlgorithmsModalData] =
    useState<ModalData | null>(null);
  const [sourcesModalData, setSourcesModalData] = useState<ModalData | null>(
    null,
  );
  const [frequency, setFrequency] = useState<Frequency | undefined>(undefined);

  const {
    selectedValueDateRange,
    descriptionOptions,
    selectedDescription,
    isFetchingDescriptions,
    dataObjectVersions,
    isFetchingDataObjectVersions,
    refetchDescriptions,
  } = useTimeSeriesSelection(queryParameters, setQueryFilters);
  const descriptionId = selectedDescription?.descriptionId;

  const {
    data: { content, totalElements } = EMPTY_PAGE,
    isFetching,
    refetch,
  } = useValidationDashboardsQuery(
    {
      ...queryParameters,
      dataObjectDescriptionId: descriptionId!,
      dashboardType: DashboardType.TIME_SERIES,
    },
    {
      placeholderData: keepPreviousData,
      enabled: !!descriptionId,
    },
  );

  const getRequestData = useCallback(() => {
    const [
      dataObjectVersionIds,
      valueDateRange,
      ruleCode,
      outliers,
      hodricPrescotti,
    ] = getValues([
      'dataObjectVersionIds',
      'valueDateRange',
      'versionRule',
      'outliers',
      'hodricPrescotti',
    ]);
    return {
      dataObjectVersionIds,
      valueDateFrom: valueDateRange[0],
      valueDateTo: valueDateRange[1],
      ruleCode,
      outliers: Boolean(outliers),
      hp: Boolean(hodricPrescotti),
    };
  }, [getValues]);

  const openDetailsModal = useCallback(
    (
      modalType: 'algorithms' | 'sources',
      rowData?: ValidationDashboardListDto,
    ) => {
      if (!selectedDescription) return;
      const versions = rowData
        ? rowData.versions.map((v) => v.dataObjectVersion)
        : dataObjectVersions;
      if (!versions.length) return;
      const data = {
        type: DashboardType.TIME_SERIES,
        versions,
        descriptionOption: selectedDescription,
      } as const;
      if (modalType === 'algorithms') setAlgorithmsModalData(data);
      else setSourcesModalData(data);
    },
    [selectedDescription, dataObjectVersions],
  );

  const {
    createDashboard,
    openDashboard,
    duplicateDashboard,
    changeDashboardData,
  } = useVisualValidationDashboardActions<TimeSeriesRequestData>({
    hasAccess: selectedDescription?.hasAccess || false,
    popupWindowPath: path.visualValidationGenerating,
    getRequestData,
    useCreate: useGenerateVisualValidationTimeSeriesMutation,
    useDuplicate: useDuplicateVisualValidationTimeSeriesMutation,
    useChangeData: useChangeVisualValidationTimeSeriesDataMutation,
    onValidationFailed: trigger,
    onSuccess: refetch,
    setLoading: setIsLoadingDashboard,
    getDashboardType,
  });

  return (
    <Main.Col>
      <Main.Header />
      <Main.Content padding={false} id="visual-validation-time-series">
        {isCommentsModalOpen && selectedDescription && (
          <CommentsModal
            descriptionOption={selectedDescription}
            type="range"
            valueDates={selectedValueDateRange}
            refetchDescriptions={refetchDescriptions}
            onClose={() => setIsCommentsModalOpen(false)}
          />
        )}
        {algorithmsModalData && (
          <AlgorithmsModal
            {...algorithmsModalData}
            onClose={() => setAlgorithmsModalData(null)}
          />
        )}
        {sourcesModalData && (
          <SourcesModal
            {...sourcesModalData}
            onClose={() => setSourcesModalData(null)}
          />
        )}
        <ContentWrapper>
          <VisualValidationHeader
            infoIconUrl={InfoIconUrl.VISUAL_VALIDATION_TIME_SERIES}
          />

          <Padding horizontal>
            <DataObjectDescriptionFilter
              onFrequencyChange={(frequency) => {
                setFrequency(frequency);
              }}
              options={descriptionOptions}
              isLoading={isFetchingDescriptions}
            />
          </Padding>
          <Divider />
          <Padding horizontal bottom>
            <Flex gap="small" wrap="wrap" justify="space-between">
              <Flex gap="small" wrap="wrap">
                <TimeSeriesFilters
                  disabled={!frequency}
                  frequency={frequency}
                  isTimeSeriesView
                />
              </Flex>
              <VisualValidationActionButtons
                hasDescription={!!descriptionId}
                hasVersions={dataObjectVersions.length > 0}
                hasComments={selectedDescription?.hasComments || false}
                onCommentsClick={() => {
                  setIsCommentsModalOpen(true);
                }}
                onAlgorithmsClick={() => openDetailsModal('algorithms')}
                onSourcesClick={() => openDetailsModal('sources')}
                onNewDashboardClick={() => handleSubmit(createDashboard)()}
                filtersCount={headerProps.filtersCount}
                onFilterClear={headerProps.onFilterClear}
                isLoading={isFetchingDataObjectVersions || isLoadingDashboard}
              />
            </Flex>
          </Padding>
          <Padding horizontal bottom>
            <Flex vertical gap="small">
              <ValidationDashboardsTable
                data={content || []}
                isLoading={isFetching}
                setFilters={setTableFilters}
                scrollContainerSelector="#visual-validation-time-series"
                refetchData={refetch}
                onClickOpen={openDashboard}
                onClickDuplicate={duplicateDashboard}
                onClickChangeData={changeDashboardData}
                onClickSourceData={(d) => openDetailsModal('sources', d)}
                onClickAlgorithmData={(d) => openDetailsModal('algorithms', d)}
                {...tableProps}
              />
            </Flex>
          </Padding>
        </ContentWrapper>
      </Main.Content>
      <Main.Footer>
        <TotalEntries count={totalElements} />
        <FooterPagination {...paginationProps} />
      </Main.Footer>
    </Main.Col>
  );
};

export const useTimeSeriesSelection = (
  queryParameters: Partial<TimeSeriesFilters>,
  setQueryFilters: (filters: Partial<TimeSeriesFilters>) => void,
) => {
  const { reset, setValue } = useFormContext<TimeSeriesFormType>();
  const {
    groupDescriptionRef,
    valueDateRange,
    versionRule,
    dataObjectVersionIds,
    outliers,
    hodricPrescotti,
  } = useWatch<TimeSeriesFormType>();

  // Update query parameters when form values change
  useEffect(() => {
    setQueryFilters({
      groupDescriptionRef,
      valueDateFrom: valueDateRange?.[0],
      valueDateTo: valueDateRange?.[1],
      versionRule: versionRule,
    });
  }, [groupDescriptionRef, setQueryFilters, valueDateRange, versionRule]);

  // Reset form values when query parameters change
  useEffect(() => {
    const valueDateFromParam = queryParameters?.valueDateFrom;
    const valueDateToParam = queryParameters?.valueDateTo;
    const versionRuleParam = queryParameters?.versionRule;

    reset({
      ...queryParameters,
      valueDateRange: [
        (valueDateFromParam as ISODate) || undefined,
        (valueDateToParam as ISODate) || undefined,
      ],
      versionRule: versionRuleParam,
      outliers,
      hodricPrescotti,
    });
  }, [hodricPrescotti, outliers, queryParameters, reset, setValue]);

  const {
    data: dataObjectGroupsWithDescriptions,
    refetch: refetchDescriptions,
    isFetching: isFetchingDescriptions,
  } = useDataObjectGroupsWithDescriptionsQuery(
    {
      groupType: DataObjectGroupType.O,
    },
    {
      placeholderData: keepPreviousData,
    },
  );
  const descriptionOptions = useGroupDescriptionOptions(
    dataObjectGroupsWithDescriptions,
    false,
  );

  const selectedDescription = useMemo(
    () =>
      descriptionOptions
        .flatMap((options) => options)
        .filter(hasDescriptionId)
        .find((option) => option.value === groupDescriptionRef),
    [descriptionOptions, groupDescriptionRef],
  );
  const descriptionId = selectedDescription?.descriptionId;

  const {
    data: dataObjectVersions = [],
    isFetching: isFetchingDataObjectVersions,
  } = useLatestDataObjectVersionsQuery(
    {
      descriptionId: descriptionId!,
      valueDateFrom: valueDateRange?.[0]!,
      valueDateTo: valueDateRange?.[1]!,
      ruleCode: versionRule!,
    },
    {
      enabled: Boolean(
        descriptionId &&
          valueDateRange?.[0] &&
          valueDateRange?.[1] &&
          versionRule,
      ),
    },
  );

  useEffect(() => {
    const newVersionIds = dataObjectVersions.map((ver) => ver.id);
    const same =
      dataObjectVersionIds &&
      dataObjectVersionIds.length === newVersionIds.length &&
      dataObjectVersionIds.every((id, index) => id === newVersionIds[index]);
    if (!same) {
      setValue('dataObjectVersionIds', newVersionIds);
    }
  }, [setValue, dataObjectVersionIds, dataObjectVersions]);

  return {
    selectedValueDateRange: valueDateRange,
    descriptionOptions,
    selectedDescription,
    isFetchingDescriptions,
    refetchDescriptions,
    dataObjectVersions,
    isFetchingDataObjectVersions,
  };
};

function getDashboardType(request: TimeSeriesRequestData) {
  if (request.hp) {
    return DashboardType.HODRICK_PRESCOTT;
  }
  if (request.outliers) {
    return DashboardType.OUTLIERS;
  }

  return DashboardType.TIME_SERIES;
}

const FooterPagination = styled(CustomPagination)`
  margin-left: auto;
`;

const ContentWrapper = styled(Stack)`
  width: 100%;
  max-width: 120em;
  height: 100%;
  margin-right: auto;
  margin-left: auto;
`;
